import { useCallback, useEffect, useRef, useState } from 'react';
import { voiceApi } from '../services/voiceApi';

export type VoiceState = 'idle' | 'recording' | 'transcribing';

interface UseVoiceInputOptions {
  onTranscript: (text: string) => void;
  onError?: (message: string) => void;
  /** Hard cap so recording is bounded even in sustained noise (spec §5.1). */
  maxRecordSeconds?: number;
}

const PREFERRED_MIME = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined;
  return PREFERRED_MIME.find((m) => MediaRecorder.isTypeSupported(m));
}

function extForMime(mime: string): string {
  if (mime.startsWith('audio/mp4')) return 'm4a';
  if (mime.startsWith('audio/webm')) return 'webm';
  return 'webm';
}

export function useVoiceInput({ onTranscript, onError, maxRecordSeconds = 20 }: UseVoiceInputOptions) {
  const supported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';

  const [state, setState] = useState<VoiceState>('idle');
  const [micLevel, setMicLevel] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mimeRef = useRef<string>('audio/webm');
  const startingRef = useRef(false);

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    rafRef.current = null;
    capTimerRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    setMicLevel(0);
  }, []);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop(); // fires onstop -> transcribe
    }
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    cleanup();
    setState('idle');
  }, [cleanup]);

  const start = useCallback(async () => {
    if (!supported || state !== 'idle') return;
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime() ?? '';
      mimeRef.current = mime || 'audio/webm';
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mimeRef.current = recorder.mimeType || mimeRef.current;
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        if (blob.size === 0) {
          setState('idle');
          onError?.("Didn't catch that");
          return;
        }
        setState('transcribing');
        abortRef.current = new AbortController();
        try {
          const { text } = await voiceApi.transcribe(
            blob,
            `audio.${extForMime(mimeRef.current)}`,
            abortRef.current.signal,
          );
          setState('idle');
          if (text?.trim()) onTranscript(text.trim());
          else onError?.("Didn't catch that");
        } catch (err: any) {
          setState('idle');
          if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
            onError?.("Couldn't transcribe — try again or type");
          }
        }
      };

      // AudioContext for the opportunistic level meter; resume() inside the
      // user gesture (iOS starts it suspended). Degrade silently if it can't.
      try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new Ctx();
        await ctx.resume();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(buf);
          const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
          setMicLevel(avg / 255);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        /* no meter — manual stop still works */
      }

      recorder.start();
      setState('recording');
      // Hard cap: manual stop is primary, but always bound the recording.
      capTimerRef.current = setTimeout(() => stop(), maxRecordSeconds * 1000);
      startingRef.current = false;
    } catch (err: any) {
      startingRef.current = false;
      cleanup();
      setState('idle');
      onError?.(
        err?.name === 'NotAllowedError'
          ? 'Microphone access denied — enable it in settings'
          : "Couldn't start recording",
      );
    }
  }, [supported, state, cleanup, stop, onTranscript, onError, maxRecordSeconds]);

  useEffect(() => () => { abortRef.current?.abort(); cleanup(); }, [cleanup]);

  return { supported, state, micLevel, start, stop, abort };
}
